// Example templates shown on the Upload screen. Keep these in sync with the
// server parser in medflyt_server2 (AuthFileCtrl.ts / AuthFilePatchCtrl.ts) —
// column headers are matched case-insensitively and whitespace-insensitively,
// so human-readable names like "Patient Display ID" map to the parser's fields.

// One authorization file carries both kinds of row, and a column's
// requirement usually differs between them, so each column documents both.
// The screen has a switch to flip which one is shown.
export type UploadRowMode = "new" | "update";

// "one-of" means exactly one of a paired set (e.g. Contract / Contract ID) is
// required, not that the column is optional. "conditional" is required only
// alongside some other column; "identifier" is read to find or confirm the
// row's authorization and is never written; "n-a" does not apply to this kind
// of row at all; "not-supported" columns are rejected.
export type UploadColumnRequirement =
  | "required"
  | "optional"
  | "one-of"
  | "conditional"
  | "identifier"
  | "n-a"
  | "not-supported";

export interface UploadColumnDoc {
  name: string;
  /** Requirement for a row that creates a new authorization. */
  requirement: UploadColumnRequirement;
  /** Requirement for a row that updates an existing one. */
  updateRequirement: UploadColumnRequirement;
  description: string;
  /** Shown instead of `description` when the screen is in "update" mode. */
  updateDescription?: string;
}

/**
 * Prose rules that do not fit the per-column table — how a row picks between
 * creating and updating, empty-cell semantics, and what an update does to
 * visits and allocations once it runs.
 */
export interface UploadRuleSection {
  title: string;
  items: string[];
  /** Only shown while the screen is in this mode; omit for "always". */
  mode?: UploadRowMode;
}

export interface UploadTypeDoc {
  key: string;
  title: string;
  description: string;
  fileName: string;
  columns: UploadColumnDoc[];
  csv: string;
  rules?: UploadRuleSection[];
  /** Enables the new/update switch above the column table. */
  hasRowModes?: boolean;
}

const AUTHORIZATION_COLUMNS: UploadColumnDoc[] = [
  {
    name: "Patch Target",
    requirement: "n-a",
    updateRequirement: "required",
    description: "Leave empty. An empty cell is what makes the row a new authorization.",
    updateDescription:
      "Which authorization to update: its id, or LATEST_MATCH / LATEST_MATCH_DATE. No match is a row error — an update never falls back to creating.",
  },
  {
    name: "Patch Empty",
    requirement: "n-a",
    updateRequirement: "optional",
    description: "Ignored on new rows — a new authorization has nothing to leave alone.",
    updateDescription:
      "AS_IS (default) leaves an empty cell's field untouched. NULL clears it. Blank means AS_IS, the non-destructive reading.",
  },
  {
    name: "Patient Display ID",
    requirement: "one-of",
    updateRequirement: "identifier",
    description: "Patient's display id. Provide this OR Patient ID.",
    updateDescription:
      "Required with LATEST_MATCH / LATEST_MATCH_DATE (part of the match key). With an id, optional and checked — a mismatch errors the row rather than updating someone else's authorization.",
  },
  {
    name: "Patient ID",
    requirement: "one-of",
    updateRequirement: "identifier",
    description: "Patient's internal id. Provide this OR Patient Display ID.",
    updateDescription: "Same as Patient Display ID.",
  },
  {
    name: "Contract",
    requirement: "one-of",
    updateRequirement: "identifier",
    description: "Contract (type) name. Provide this OR Contract ID.",
    updateDescription:
      "Match key or confirmation, as above. An update can never move an authorization to a different contract.",
  },
  {
    name: "Contract ID",
    requirement: "one-of",
    updateRequirement: "identifier",
    description: "Contract (type) id. Provide this OR Contract.",
    updateDescription: "Same as Contract.",
  },
  {
    name: "Service Code",
    requirement: "one-of",
    updateRequirement: "identifier",
    description: "Service code (e.g. PCA, HHA). Provide this OR Service Code ID.",
    updateDescription:
      "Match key or confirmation, as above. An update can never change an authorization's service code.",
  },
  {
    name: "Service Code ID",
    requirement: "one-of",
    updateRequirement: "identifier",
    description: "Service code id. Provide this OR Service Code.",
    updateDescription: "Same as Service Code.",
  },
  {
    name: "Auth Code",
    requirement: "optional",
    updateRequirement: "optional",
    description: "Authorization number from the auth document.",
    updateDescription: "Authorization number. Cleared when empty under NULL.",
  },
  {
    name: "External ID",
    requirement: "not-supported",
    updateRequirement: "optional",
    description: "Not imported on new rows — a new authorization is always created without one.",
    updateDescription:
      "External system id. Cleared when empty under NULL — which breaks the link to that system, so prefer AS_IS unless you mean it.",
  },
  {
    name: "Start Date",
    requirement: "required",
    updateRequirement: "optional",
    description: "First valid day. MM/DD/YYYY or YYYY-MM-DD.",
    updateDescription:
      "New first valid day. Never cleared (empty always means unchanged). Under LATEST_MATCH_DATE this identifies the authorization instead.",
  },
  {
    name: "End Date",
    requirement: "required",
    updateRequirement: "optional",
    description: "Last valid day. MM/DD/YYYY or YYYY-MM-DD.",
    updateDescription: "New last valid day. Same rules as Start Date.",
  },
  {
    name: "Period Type",
    requirement: "required",
    updateRequirement: "optional",
    description: "One of: Daily, Weekly, Monthly, Entire Period.",
    updateDescription:
      "Present replaces the whole time config and makes every child column below required. Absent leaves the time config alone — and then any hours column is a row error.",
  },
  {
    name: "Total Hours",
    requirement: "optional",
    updateRequirement: "conditional",
    description:
      "Total hours for the whole authorization. Used for Entire Period; caps other types. Blank defaults to 999,999 minutes.",
    updateDescription:
      "Required when Patch Empty is NULL, and when Period Type is Entire Period. Empty under AS_IS keeps the current value.",
  },
  {
    name: "Monday Hours … Sunday Hours",
    requirement: "optional",
    updateRequirement: "conditional",
    description: "Hours per weekday. Used when Period Type = Daily.",
    updateDescription:
      "All seven are required when Period Type = Daily. Enter 0 for days with no hours — a blank would be read as 0 and silently drop that day.",
  },
  {
    name: "Weekly Hours",
    requirement: "conditional",
    updateRequirement: "conditional",
    description: "Hours per week. Required when Period Type = Weekly.",
    updateDescription: "Required when Period Type = Weekly.",
  },
  {
    name: "First Day Of Week",
    requirement: "optional",
    updateRequirement: "conditional",
    description: "Weekly only. MONDAY..SUNDAY. Defaults to MONDAY.",
    updateDescription:
      "Required when Period Type = Weekly (MONDAY..SUNDAY). Unlike a new row this has no default, so the week start is never silently reset.",
  },
  {
    name: "Monthly Hours",
    requirement: "conditional",
    updateRequirement: "conditional",
    description: "Hours per month. Required when Period Type = Monthly.",
    updateDescription: "Required when Period Type = Monthly.",
  },
  {
    name: "Restriction 1/2 Days + Hours Per Day",
    requirement: "optional",
    updateRequirement: "optional",
    description:
      "Weekly only. Optional per-day cap (e.g. 2 days at 5h). Up to 2 restrictions; they should sum to Weekly Hours.",
    updateDescription:
      "Weekly only. The one part of the time config still governed by Patch Empty: left blank they are kept under AS_IS and cleared under NULL.",
  },
  {
    name: "Office Override",
    requirement: "optional",
    updateRequirement: "optional",
    description: "Office name to route created visits to (optional).",
    updateDescription: "Office to route created visits to. Cleared when empty under NULL.",
  },
  {
    name: "Create Visits On Past Dates",
    requirement: "n-a",
    updateRequirement: "optional",
    description: "Not used on new rows — creating an authorization always backfills past dates.",
    updateDescription:
      "Yes/No, default No. Only matters when the start date moves earlier, where Yes lets the rollover backfill visits from the weekly template.",
  },
  {
    name: "Notes",
    requirement: "not-supported",
    updateRequirement: "not-supported",
    description: "Not imported. The parser ignores this column on new rows.",
    updateDescription:
      "Rejected on update rows. Authorization notes cannot be patched — remove the column or leave the cell empty.",
  },
];

const AUTHORIZATION_RULES: UploadRuleSection[] = [
  {
    title: "New rows and update rows in one file",
    items: [
      "A row with an empty Patch Target creates a new authorization. A row with one filled in updates the authorization it names. You can mix both in the same file.",
      "Rows are independent: one bad row is reported on its own and never blocks the rest.",
      "Every row is matched against the authorizations that existed when the file was uploaded, so an update row can never target an authorization created by a new row in the same file.",
    ],
  },
  {
    title: "Choosing which authorization to update",
    mode: "update",
    items: [
      "An authorization id updates exactly that authorization. The id is the first column of the Authorizations table on the patient page.",
      "LATEST_MATCH picks the newest (highest id) authorization for the row's patient + contract + service code, so those three columns are required.",
      "LATEST_MATCH_DATE narrows that by Start Date and End Date as well. Those two columns then identify the authorization, which means such a row cannot also change the dates — name the authorization by id to move its dates.",
      "“Newest” means most recently created, not the one covering the latest period. Re-running a file after someone adds an authorization by hand can therefore target the new one.",
      "If nothing matches, the row errors. An update never falls back to creating — and LATEST_MATCH cannot see authorizations that new rows in the same file are about to create.",
    ],
  },
  {
    title: "Empty cells",
    mode: "update",
    items: [
      "AS_IS (the default) means an empty cell changes nothing. NULL means an empty cell clears the field.",
      "Only Auth Code, External ID, Office Override and the weekly restrictions can actually be cleared. Everything else is stored NOT NULL.",
      "Because of that, an empty Total Hours under NULL is a row error rather than a guess, and dates are never cleared under either mode.",
      "NULL is exactly how a new row reads a blank cell, which is why new rows need no mode.",
    ],
  },
  {
    title: "Hours and period type",
    mode: "update",
    items: [
      "A time config is replaced whole, never merged: it is one setting spread across the period type, the period minutes, seven weekday columns, the week start and the restrictions.",
      "So supply Period Type with every one of its children, or leave Period Type empty and the whole config is kept.",
      "Any hours column filled in without a Period Type is a row error, not a silent no-op.",
      "To change one weekday you must restate all seven. That is deliberate — a blank weekday reads as 0 hours, which would drop days the row never mentioned.",
      "Switching period type is lossy: moving to Weekly or Monthly clears the seven weekday values, and moving back cannot recover them.",
    ],
  },
  {
    title: "What an update does beyond the authorization row",
    mode: "update",
    items: [
      "Extending the dates creates visits from the patient's weekly template; shortening them removes future or past visits that are not billed, paid, invoiced, on a payroll draft, clocked in, or linked to PTO.",
      "Authorization allocations are then re-solved for every visit the patient has on that contract type and service code — with no date limit — so a change can move minutes on visits belonging to other authorizations.",
      "Billed and paid visits keep their existing allocations, and those minutes still count against the authorization.",
    ],
  },
];

const AUTHORIZATION_CSV = `Patch Target,Patch Empty,Patient Display ID,Patient ID,Contract,Contract ID,Service Code,Service Code ID,Auth Code,External ID,Start Date,End Date,Period Type,Total Hours,Monthly Hours,Weekly Hours,Monday Hours,Tuesday Hours,Wednesday Hours,Thursday Hours,Friday Hours,Saturday Hours,Sunday Hours,First Day Of Week,Restriction 1 Days,Restriction 1 Hours Per Day,Restriction 2 Days,Restriction 2 Hours Per Day,Office Override,Create Visits On Past Dates,Comment
,,1001,,Medicaid,,PCA,,AUTH-1001,,01/01/2025,12/31/2025,Weekly,,,40,,,,,,,,MONDAY,,,,,,,NEW — weekly 40 hours (by name / display id)
,,1002,,Medicaid,,HHA,,AUTH-1002,,02/01/2025,06/30/2025,Daily,,,,8,8,8,8,8,0,0,,,,,,,,NEW — weekday 8 hours
,,,501,,3,,12,AUTH-1003,,03/01/2025,03/31/2026,Monthly,,160,,,,,,,,,,,,,,,,NEW — by ids (Patient ID / Contract ID / Service Code ID)
,,1004,,Medicaid,,PCA,,,,01/01/2025,12/31/2025,Entire Period,200,,,,,,,,,,,,,,,,,NEW — fixed 200 hour pool
4512,AS_IS,,,,,,,,,,12/31/2026,,,,,,,,,,,,,,,,,,,UPDATE — end date only; AS_IS leaves every other field alone
4513,AS_IS,1002,,Medicaid,,HHA,,AUTH-1002-R2,,,,,,,,,,,,,,,,,,,,,,"UPDATE — Patient/Contract/Service Code are checked, never changed"
4514,AS_IS,,,,,,,,,,,Daily,,,,10,8,8,8,8,0,0,,,,,,,,"UPDATE — replaces the whole Daily config; every weekday required, 0 where there are none"
4515,NULL,,,,,,,,,,,,2000,,,,,,,,,,,,,,,,,UPDATE — NULL clears Auth Code / External ID / Office Override; Total Hours then required
LATEST_MATCH,AS_IS,1001,,Medicaid,,PCA,,,,,,Weekly,,,45,,,,,,,,MONDAY,,,,,,,UPDATE — newest authorization for this patient + contract + service code
LATEST_MATCH_DATE,AS_IS,1005,,Medicaid,,PCA,,,,01/01/2025,12/31/2025,,,,,,,,,,,,,,,,,Main Office,,UPDATE — the dates identify the authorization, so this row cannot also change them
`;

export const UPLOAD_TYPES: UploadTypeDoc[] = [
  {
    key: "AR_NOTES",
    title: "AR notes",
    description:
      "Upload a CSV or Excel file to create notes for visits. Valid rows are queued; errors appear in Uploads, where you can download and retry them.",
    fileName: "ar_notes_example.csv",
    csv: "visit_instance_id,no_payment_reason,collection_status,follow_up_date,follow_up_by_id,note\n12345,Replace with a valid reason,Replace with a valid collection status,12/31/2026,Jane Smith,Follow up with payer\n",
    columns: [
      {
        name: "visit_instance_id",
        requirement: "required",
        updateRequirement: "n-a",
        description: "Visit ID belonging to this agency.",
      },
      {
        name: "no_payment_reason",
        requirement: "required",
        updateRequirement: "n-a",
        description: "Exact active AR reason text from Download valid reasons and refs.",
      },
      {
        name: "collection_status",
        requirement: "required",
        updateRequirement: "n-a",
        description: "Exact active collection status text from the reference file.",
      },
      {
        name: "follow_up_date",
        requirement: "conditional",
        updateRequirement: "n-a",
        description: "MM/DD/YYYY or an Excel date. Required when a follow-up member is supplied.",
      },
      {
        name: "follow_up_by_id",
        requirement: "conditional",
        updateRequirement: "n-a",
        description:
          "Agency member ID or full name (case insensitive). Required with a follow-up date. Duplicate names require an ID. The header follow_up_by_name is also accepted.",
      },
      {
        name: "note",
        requirement: "required",
        updateRequirement: "n-a",
        description: "Text of the new AR note.",
      },
    ],
    rules: [
      {
        title: "Before uploading",
        items: [
          "Replace the example values with visits and references from your agency.",
          "Leave both follow-up fields empty when no follow-up is needed.",
          "Each valid row creates a new note. Uploading the same file again creates more notes; retry errors to process only failed rows.",
          "Files without headers use the six columns in the order shown above.",
        ],
      },
    ],
  },
  {
    key: "AUTH_FILE",
    title: "Authorizations",
    description:
      "Create new patient authorizations and update existing ones from a single file. A row with an empty Patch Target creates; a row with one filled in updates the authorization it names. Rows that fail validation are reported per-row without blocking the rest.",
    fileName: "authorization_example.csv",
    columns: AUTHORIZATION_COLUMNS,
    csv: AUTHORIZATION_CSV,
    rules: AUTHORIZATION_RULES,
    hasRowModes: true,
  },
];

export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
